import React, { useState } from 'react';
import { X, Plus, Calendar, Trash2 } from 'lucide-react';
import { ClientWeight } from '../types';
import { useClientWeights } from '../hooks/useClients';
import WeightAdjuster from './WeightAdjuster';
import WeightChart from './WeightChart';

interface WeightModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
}

const WeightModal: React.FC<WeightModalProps> = ({ isOpen, onClose, clientId }) => {
  const { weights, isLoading, addWeight, isAddLoading, deleteWeight } = useClientWeights(clientId);
  const [newWeight, setNewWeight] = useState<number>(weights && weights[0] ? parseFloat(weights[0].weight.toFixed(1)) : 70);
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));

  React.useEffect(() => {
    if (weights && weights[0]) {
      setNewWeight(parseFloat(weights[0].weight.toFixed(1)));
    }
  }, [weights]);

  const handleAdd = () => {
    addWeight({ weight: newWeight, date: new Date(date).toISOString() });
  };

  // Проверяем, есть ли уже запись веса за выбранную дату
  const existingWeightForDate = weights?.find((w: any) => {
    // Используем более надежный способ форматирования даты
    const weightDate = new Date(w.date + 'Z').toISOString().slice(0, 10);
    return weightDate === date;
  });

  const buttonText = 'Добавить запись веса';

  const handleDelete = (w: ClientWeight) => {
    const dateLabel = new Date(w.date).toLocaleDateString('ru-RU');
    if (window.confirm(`Удалить запись веса ${w.weight} кг за ${dateLabel}? Восстановить её будет нельзя.`)) {
      deleteWeight(w.id);
    }
  };

  if (!isOpen) return null;

  const last = weights && weights[0];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Вес клиента</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {last ? (
                <div className="mb-4">
                  <div className="text-sm text-gray-500 mb-1">Последняя запись:</div>
                  <div className="flex items-center space-x-2">
                    <span className="font-bold text-lg">{last.weight} кг</span>
                    <span className="flex items-center text-xs text-gray-400">
                      <Calendar size={14} className="mr-1" />
                      {new Date(last.date).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="mb-4 text-gray-400">Нет записей веса</div>
              )}
              {/* График веса */}
              {weights && weights.length > 0 && (
                <div className="mb-4">
                  <WeightChart data={weights} />
                </div>
              )}
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Дата</label>
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {existingWeightForDate && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-700">
                      ⚠️ Запись веса за эту дату уже существует. При сохранении она будет обновлена.
                    </p>
                  </div>
                )}
              </div>
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Вес (кг)</label>
                <WeightAdjuster weight={Number(newWeight.toFixed(1))} onWeightChange={w => setNewWeight(Number(w.toFixed(1)))} step={0.1} />
              </div>
              <button
                onClick={handleAdd}
                disabled={isAddLoading}
                className="w-full mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isAddLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Добавление...</span>
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    <span>{buttonText}</span>
                  </>
                )}
              </button>

              {/* История */}
              {weights && weights.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">История</h3>
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                    {weights.map((w: ClientWeight) => (
                      <li key={w.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div>
                          <div className="text-gray-900">{w.weight.toFixed(1)} кг</div>
                          <div className="text-xs text-gray-400">{new Date(w.date).toLocaleDateString('ru-RU')}</div>
                        </div>
                        <button
                          onClick={() => handleDelete(w)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Удалить"
                        >
                          <Trash2 size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeightModal; 